import type { ReactNode } from 'react'
import { createContext, useContext } from 'react'
import { observable, runInAction } from 'mobx'
import type { IObservableValue, ObservableMap } from 'mobx'

type Callback = () => void | boolean | Promise<void | boolean>
type LabelOrComponent = string | ReactNode
type QAToolsContextType = {
  enabled: IObservableValue<boolean>
  hide: () => void
  show: () => void
  toggle: () => void
  tools: ObservableMap<
    string,
    {
      label: LabelOrComponent
      callback: Callback
    }
  >
  register: (name: string, label: LabelOrComponent, callback?: Callback) => void
  unregister: (name: string) => void
  changeLabel: (name: string, label: string) => void
}

export const QAToolsContext = createContext<QAToolsContextType | null>(null)

const noopTools: QAToolsContextType = {
  enabled: observable.box(false),
  tools: observable.map(),
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  register: (name: string, label: LabelOrComponent, callback?: Callback) => {},
  unregister: () => {},
  changeLabel: () => {},
  show: () => {}, // No-op
  hide: () => {}, // No-op
  toggle: () => {}, // No-op,
}

export const activeTools: QAToolsContextType = {
  enabled: observable.box(false),
  tools: observable.map<
    string,
    {
      label: LabelOrComponent
      callback: Callback
    }
  >(),
  register(name: string, label: LabelOrComponent, callback?: Callback) {
    runInAction(() => {
      this.tools.set(name, { label, callback: callback || (() => {}) })
    })
  },
  unregister(name: string) {
    runInAction(() => {
      this.tools.delete(name)
    })
  },
  changeLabel(name: string, label: LabelOrComponent) {
    const tool = this.tools.get(name)
    if (tool) {
      const newTool = { ...tool, label }
      runInAction(() => {
        this.tools.set(name, newTool)
      })
    }
  },
  show() {
    runInAction(() => {
      this.enabled.set(true)
    })
  },
  hide() {
    runInAction(() => {
      this.enabled.set(false)
    })
  },
  toggle() {
    runInAction(() => {
      this.enabled.set(!this.enabled.get())
    })
  },
}

export function useQATools() {
  const context = useContext(QAToolsContext)

  if (!context) {
    return noopTools
  }
  return context
}
